import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {extractEmailDomain} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {InlineBanner, InlineBannerTypesEnum} from '../../components/inlineBanner/inlineBanner';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {ChannelModel} from '../../models/api/channelModel';
import {InboxModel} from '../../models/api/inboxModel';
import {postAsync} from '../../services/http/http';
import {InterfaceEventsEnum} from '../../services/tracking';
import {getAdminDnsDomains} from '../../store/data/channels/channelsSelectors';
import {getCompanyPreferences} from '../../store/data/company/companySelectors';
import {getIsUserGlobalAdmin, getUserNamespace} from '../../store/data/teammates/teammatesSelectors';
import {openHelpChatAction} from '../../store/panels/app/appPanelActions';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {makeSettingsUrlFromPathId} from '../menu/settingsHelpers';
import {SpfDkimHelpDialog} from './channels/email/settingsSpfDkimField';
import {useChannelEditSettingsEvent, useReauthorizeChannel} from './channels/settingsChannelHooks';
import {ChannelWarningsEnum, getWarningProps, getWarningsForChannel} from './settingsChannelWarningHelpers';

/*
 * Props.
 */

interface SettingsChannelWarningBannerProps {
  channel: ChannelModel;
  inbox: InboxModel;
  panelId: SettingsPanelId;
}

/*
 * Style.
 */

const StyledAnchor = styled.a`
  color: inherit;
  text-decoration: underline;
`;

/*
 * Component.
 */

export const SettingsChannelWarningBanner: FC<SettingsChannelWarningBannerProps> = (props) => {
  const userNamespace = useAppSelector(getUserNamespace);
  const companyPreferences = useAppSelector(getCompanyPreferences);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const channelWarnings = getWarningsForChannel(props.channel, userNamespace, companyPreferences) || [];
  const channelWarningProps = channelWarnings.map(
    (warning) => [warning, getWarningProps(warning, isUserGlobalAdmin)] as const,
  );

  const dispatch = useAppDispatch();
  const [showSmtpValidationDialog, setShowSmtpValidationDialog] = useState(false);
  const [showOfficeValidationDialog, setShowOfficeValidationDialog] = useState(false);
  const [showSpfDkimDialog, setShowSpfDkimDialog] = useState(false);
  const reauthorizeChannel = useReauthorizeChannel(props.channel);
  const {navigateToLink} = useLinkNavigation();

  const dnsDomains = useAppSelector(getAdminDnsDomains);

  const onCtaClick = (warning: ChannelWarningsEnum) => {
    if (warning === ChannelWarningsEnum.SLOW_LANE) {
      dispatch(openHelpChatAction(props.panelId));
    } else if (warning === ChannelWarningsEnum.NEEDS_VALIDATION) {
      setShowSmtpValidationDialog(true);
    } else if (warning === ChannelWarningsEnum.O365_SEND_DENIED) {
      setShowOfficeValidationDialog(true);
    } else if (
      warning === ChannelWarningsEnum.O365_ACCESS_DENIED ||
      warning === ChannelWarningsEnum.DENIED_OTHER ||
      warning === ChannelWarningsEnum.O365_INVALID_FOLDERS ||
      warning === ChannelWarningsEnum.GMAIL_SERVICE_NOT_ENABLED ||
      warning === ChannelWarningsEnum.CALENDAR_CREDENTIALS
    ) {
      reauthorizeChannel();
    } else if (warning === ChannelWarningsEnum.SPF_DKIM_WARNING) {
      if (isUserGlobalAdmin) {
        const domainId = dnsDomains.findIndex((domain) => extractEmailDomain(props.channel.sendAddress));

        if (domainId < 0) {
          return;
        }

        navigateToLink(makeSettingsUrlFromPathId('settings.deliverability.domain', {domain_id: domainId}));
      } else {
        setShowSpfDkimDialog(true);
      }
    }
  };

  const trackChannelSettingEvent = useChannelEditSettingsEvent();

  const maybeRenderDialog = () => {
    if (showSmtpValidationDialog) {
      return (
        <OkCancelDialog
          title={<Trans id="smtpvalidationdialog-title">Address validation</Trans>}
          okButtonLabel={<Trans id="smtpvalidationdialog-oklabel">Send validation email</Trans>}
          onOkClick={async () => {
            setShowSmtpValidationDialog(false);
            trackChannelSettingEvent(InterfaceEventsEnum.SMTP_VALIDATION_STARTED, props.inbox, props.channel);
            await postAsync(`${props.channel.url}/validate_smtp_address`);
            trackChannelSettingEvent(
              InterfaceEventsEnum.SMTP_VALIDATION_EMAIL_SENT,
              props.inbox,
              props.channel,
            );
          }}
          onRequestClose={() => {
            setShowSmtpValidationDialog(false);
            trackChannelSettingEvent(
              InterfaceEventsEnum.SMTP_VALIDATION_CANCELED,
              props.inbox,
              props.channel,
            );
          }}
        >
          <Trans id="smtpvalidationdialog-copy">
            This will send a test email to {props.channel.sendAddress} and check that it is automatically
            forwarded to {props.channel.address}.
          </Trans>
        </OkCancelDialog>
      );
    }

    if (showOfficeValidationDialog) {
      return (
        <OkCancelDialog
          title={<Trans id="officevalidationdialog-title">O365 Permission Validation</Trans>}
          okButtonLabel={<Trans id="officevalidationdialog-oklabel">Send validation email</Trans>}
          onOkClick={async () => {
            setShowOfficeValidationDialog(false);

            await postAsync(`${props.channel.url}/validate_sending_permissions`);
          }}
          onRequestClose={() => {
            setShowOfficeValidationDialog(false);
          }}
        >
          <Trans id="officevalidationdialog-copy">
            This will attempt to send a test email to {props.channel.sendAddress} and ensure that it is
            successfully sent. This email will be automatically deleted.
          </Trans>
        </OkCancelDialog>
      );
    }

    if (showSpfDkimDialog) {
      return (
        <SpfDkimHelpDialog
          onRequestClose={() => {
            setShowSpfDkimDialog(false);
          }}
          channel={props.channel}
        />
      );
    }

    return null;
  };

  return (
    <>
      {maybeRenderDialog()}
      {channelWarningProps.map(([warning, warningProps]) => (
        <InlineBanner
          hasColorfulText
          key={warning}
          size={VisualSizesEnum.SMALL}
          title={warningProps.label}
          isNewDesign
          type={InlineBannerTypesEnum.WARNING}
          onClose={() => {}}
          actions={_.compact([
            warningProps.ctaLabel && {
              id: 'cta',
              title: warningProps.ctaLabel,
              onClick: () => onCtaClick(warning),
            },
          ])}
        >
          {warningProps.details}{' '}
          {warningProps.link && (
            <StyledAnchor href={warningProps.link.url} target="_blank" rel="noopener noreferrer">
              {warningProps.link.label}
            </StyledAnchor>
          )}
        </InlineBanner>
      ))}
    </>
  );
};
